import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";

import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Formik, useFormikContext } from "formik";

import { useInView } from "react-intersection-observer";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";
import {
  getGameSystems,
  handlePagingAll,
} from "../../components/api/APIUtilities";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";

import TextFieldSelect from "../../components/utilities/TextFieldSelect";

import { cardUtility } from "../cards/CardUtil";
import CardCard, { CardImagePreview } from "../cards/components/CardCard";
import CardSearch from "../cards/components/CardSearch";
import MUITags from "../../components/utilities/MUITags";
import FirstHand from "./components/FirstHand";
import {
  delay,
  getData,
  postData,
  titleCase,
} from "../../components/utilities/UtilityFunctions";
import ArmyStats from "./components/ArmyStats";
import GameSystemPicker from "../../components/utilities/gamesystempickers/GameSystemPicker";
import {
  BCPMinus,
  BCPPlus,
  BCPXmark,
} from "../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const SINGLE_GAMETYPE = getEnvironmentVariable("SINGLE_GAMETYPE");
const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");

const imageCache = {};
const blankDeck = {
  tags: [],
  gameSystemId: SINGLE_GAME_SYSTEM || "",
  armyId: "",
  deckFormat: COMPANY_FLAG === "jasco" ? "My Hero Academia Only" : "Standard",
  characterId: "",
  character: undefined,
  master: true,
  public: true,
  armyListContent:
    COMPANY_FLAG === "jasco"
      ? {
          Character: {
            order: 1,
            qty: 0,
            cardType: "Character",
            name: "Character",
            size: { min: 1 },
            cards: {},
          },
          Action: {
            order: 2,
            qty: 0,
            cardType: "Action",
            name: "Action",
            cards: {},
          },
          Asset: {
            order: 3,
            qty: 0,
            cardType: "Asset",
            name: "Asset",
            cards: {},
          },
          Attack: {
            order: 4,
            qty: 0,
            cardType: "Attack",
            name: "Attack",
            cards: {},
          },
          Foundation: {
            order: 5,
            qty: 0,
            cardType: "Foundation",
            name: "Foundation",
            cards: {},
          },
          Side: {
            order: 6,
            qty: 0,
            name: "Side",
            size: { max: 10 },
            cards: {},
          },
        }
      : COMPANY_FLAG === "shard-bugs"
        ? {
            Bug: {
              order: 1,
              qty: 0,
              cardType: "Bug",
              name: "Bug",
              cards: {},
            },
            Action: {
              order: 2,
              qty: 0,
              cardType: "Action",
              name: "Action",
              cards: {},
            },
            "Quick Action": {
              order: 3,
              qty: 0,
              cardType: "Quick Action",
              name: "Quick Action",
              cards: {},
            },
            Support: {
              order: 4,
              qty: 0,
              cardType: "Support",
              name: "Support",
              cards: {},
            },
            Item: {
              order: 5,
              qty: 0,
              cardType: "Item",
              name: "Item",
              cards: {},
            },
          }
        : {
            Attack: {
              order: 1,
              qty: 0,
              cardType: "Attack",
              name: "Attack",
              cards: {},
            },
            Block: {
              order: 1,
              qty: 0,
              cardType: "Block",
              name: "Block",
              cards: {},
            },
          },
};

function CardModal(props) {
  const { card, setCard, cardAdd, cardRemove } = props;
  if (!card) {
    return <></>;
  }

  return (
    <Modal
      disableAutoFocus
      open={Boolean(card)}
      onClose={() => {
        setCard(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "95vh",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setCard(null);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid container spacing={2} sx={{}}>
          <Grid item xs={12} textAlign="center">
            <CardImagePreview card={card} large />
          </Grid>
          {(cardAdd || cardRemove) && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography textAlign="center" variant="h5">
                  Update Deck
                </Typography>
                <ButtonGroup fullWidth>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      cardRemove(card);
                    }}
                  >
                    <BCPMinus />
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      cardAdd(card);
                    }}
                  >
                    <BCPPlus />
                  </Button>
                </ButtonGroup>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography textAlign="center" variant="h5">
                  Update Side
                </Typography>
                <ButtonGroup fullWidth>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      cardRemove({ ...card, isSide: true });
                    }}
                  >
                    <BCPMinus />
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      cardAdd({ ...card, isSide: true });
                    }}
                  >
                    <BCPPlus />
                  </Button>
                </ButtonGroup>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Modal>
  );
}

const getCardCount = (armyListContent) => {
  let cardCount = 0;
  if (armyListContent) {
    for (let cardCategory of Object.values(armyListContent)) {
      if (cardCategory.cards && !cardCategory.excludeFromTotal) {
        for (let card of Object.values(cardCategory.cards)) {
          cardCount += card.qty;
        }
      }
    }
  }
  return cardCount;
};

export default function ArmyListBuilder(props) {
  const { t } = useTranslation();
  const { heading } = props;
  let { deckId } = useParams();
  const navigate = useNavigate();
  const cardUtil = cardUtility();

  const [loading, setLoading] = useState(true);

  const [ready, setReady] = useState(
    SINGLE_GAMETYPE !== undefined ? true : false
  );
  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  // const [deck, setDeck] = useState(null);
  const [card, setCard] = useState(null);
  const [characters, setCharacters] = useState([]);

  const [deckHeader, setDeckHeader] = useState(null);
  const [armyListContent, setArmyListContent] = useState(null);
  const [armyListValidation, setArmyListValidation] = useState(null);

  const [deckFormats, setDeckFormats] = useState(null);
  const [deckFormat, setDeckFormat] = useState(null);
  const [armies, setArmies] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  const updateHeader = (values, options) => {
    if (deckFormats) {
      var newDeckHeader = Object.assign(deckHeader || {}, values);

      setDeckFormat(deckFormats[newDeckHeader?.deckFormat]);
      setDeckHeader(newDeckHeader);
    }
  };

  const cardAdd = (card) => {
    if (!armyListContent) {
      return;
    }

    let cardType = card.isSide ? "Side" : card.cardType;

    var uDeckContent = { ...armyListContent };

    if (!uDeckContent[cardType]) {
      uDeckContent[cardType] = {
        order: Object.keys(uDeckContent).length + 1,
        name: cardType,
        cards: {},
      };
    }

    if (uDeckContent[cardType].cards[card.id] === undefined) {
      uDeckContent[cardType].cards[card.id] = {
        ...card,
        qty: 0,
      };
    }

    uDeckContent[cardType].qty++;
    uDeckContent[cardType].cards[card.id].qty++;

    if (card.banned) {
      postNotification(
        "error",
        t("notifications.errors.cardNotTournamentLegal")
      );
    }

    setArmyListContent(uDeckContent);
  };

  const cardRemove = (card) => {
    if (!armyListContent) {
      return;
    }

    let cardType = card.isSide ? "Side" : card.cardType;

    var uDeckContent = { ...armyListContent };
    uDeckContent[cardType].qty--;
    if (uDeckContent?.[cardType]?.cards?.[card.id]?.qty !== undefined) {
      uDeckContent[cardType].cards[card.id].qty--;
      if (uDeckContent[cardType].cards[card.id].qty <= 0) {
        if (card.id === deckHeader.characterId) {
          setDeckHeader({
            ...deckHeader,
            character: {},
            characterId: "",
          });
        }
        delete uDeckContent[cardType].cards[card.id];
      }
    }

    setArmyListContent(uDeckContent);
  };

  const saveArmyList = async () => {
    try {
      var sanitizedValues = {
        ...deckHeader,
        armyListContent: {},
      };

      let errors = false;
      if (COMPANY_FLAG === "jasco") {
        if (!sanitizedValues.armyId || sanitizedValues.armyId === "") {
          errors = true;
          postNotification(
            "error",
            t("notifications.errors.pleaseSelectResourceSymbol")
          );
        }

        if (
          sanitizedValues.character?.resourceSymbols?.length &&
          sanitizedValues.armyId &&
          sanitizedValues.armyId !== "" &&
          !sanitizedValues.character.resourceSymbols.filter(
            (el) => el.id === sanitizedValues.armyId
          ).length
        ) {
          errors = true;
          postNotification(
            "error",
            "The Resource Symbol selected does not match the Character selected"
          );
        }
      }

      if (errors) {
        return;
      }

      setProcessing(true);

      let sortedArmyListContent = { ...armyListContent };
      for (let cardCategoryId of Object.keys(armyListContent)) {
        let cardCategory = {
          order: armyListContent[cardCategoryId].order,
          cardType: armyListContent[cardCategoryId].cardType,
          name: armyListContent[cardCategoryId].name,
          qty: armyListContent[cardCategoryId].qty,
          cards: {},
        };
        if (armyListContent[cardCategoryId].cards) {
          sortedArmyListContent[cardCategoryId].cards = updateCardCategory(
            sortedArmyListContent[cardCategoryId].cards,
            cardCategoryId,
            sanitizedValues.characterId
          );
          for (let card of Object.values(
            sortedArmyListContent[cardCategoryId].cards
          )) {
            cardCategory.cards[card.id] = card.qty || 0;
          }
        }

        sanitizedValues.armyListContent[cardCategoryId] = cardCategory;
      }

      if (deckId) {
        let apiDeck = await postData(`/armylists/${deckId}`, {
          body: { set: sanitizedValues },
        });
        setArmyListContent(sortedArmyListContent);
        setArmyListValidation({
          listStatus: apiDeck?.listStatus,
          listErrors: apiDeck?.listErrors,
        });
        postNotification("success", t("notifications.success.deckUpdated"));
      } else {
        if (gameSystem?.id) {
          sanitizedValues.gameSystemId = gameSystem?.id;
        }
        let apiDeck = await postData(`/armylists`, {
          body: sanitizedValues,
        });
        setArmyListContent(sortedArmyListContent);

        setArmyListValidation({
          listStatus: apiDeck?.listStatus,
          listErrors: apiDeck?.listErrors,
        });
        navigate(`/deck/${apiDeck.id}/deck-builder`, { replace: false });

        postNotification("success", t("notifications.success.deckCreated"));
      }
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  const pollArmyListErrors = async () => {
    if (!deckId) return;

    try {
      var apiDeck = await getData(`/armylists/${deckId}`, {});
      if (apiDeck.listStatus?.toLowerCase() === "pending") {
        await delay(2000);
        await pollArmyListErrors();
      } else {
        if (apiDeck.listStatus === "passed") {
          setArmyListValidation(null);
          return;
        }

        if (apiDeck.listErrors?.length) {
          postNotification(
            "warning",
            t("notifications.warnings.deckContainsErrors")
          );
        }

        setArmyListValidation({
          listStatus: apiDeck.listStatus,
          listErrors: apiDeck.listErrors,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateCardCategory = (cardDict, cardCategoryId, characterId) => {
    let tempCards = Object.values(cardDict);
    let tempCardDict = {};
    tempCards.sort((a, b) => {
      if (cardCategoryId === "Character" && characterId) {
        if (a.id === characterId) {
          return -1;
        }
        if (b.id === characterId) {
          return 1;
        }
      }
      if (b.qty !== a.qty) {
        return b.qty - a.qty;
      }
      var nameA = a.name.toLowerCase();
      var nameB = b.name.toLowerCase();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;

      return 0;
    });
    for (let card of tempCards) {
      tempCardDict[card.id] = card;
    }
    return tempCardDict;
  };

  useEffect(() => {
    if (armyListValidation?.listStatus === "pending") {
      pollArmyListErrors();
    }
    // eslint-disable-next-line
  }, [armyListValidation]);

  const loadPage = async (gs) => {
    setLoading(true);

    // fetch cardUtil info from api?

    var dFormats = {};
    for (let df of cardUtil.deckFormats) {
      dFormats[df.name] = df;
    }

    var apiDeck;
    if (deckId) {
      try {
        apiDeck = await getData(`/armylists/${deckId}`, {
          queryStringParameters: {
            "expand[]": ["user", "army", "character", "gameSystem"],
          },
        });

        if (!apiDeck.gameSystem) {
          apiDeck.gameSystem = blankDeck.gameSystem;
        }

        // handle deckFormat
        if (!apiDeck.deckFormat) {
          apiDeck.deckFormat = blankDeck.deckFormat;
        }
        let apiDeckFormat = dFormats[apiDeck.deckFormat];
        setDeckFormat(apiDeckFormat);

        // handle content
        if (!apiDeck.armyListContent) {
          apiDeck.armyListContent = blankDeck.armyListContent;
        }
        for (let cardCategoryId of Object.keys(apiDeck.armyListContent)) {
          let expand = apiDeckFormat?.categories?.[cardCategoryId] || {};
          let qty = 0;
          if (apiDeck.armyListContent[cardCategoryId].cards) {
            apiDeck.armyListContent[cardCategoryId].cards = updateCardCategory(
              apiDeck.armyListContent[cardCategoryId].cards,
              cardCategoryId,
              apiDeck.characterId
            );

            for (let card of Object.values(
              apiDeck.armyListContent[cardCategoryId].cards
            )) {
              qty += card.qty || 0;
            }
          }
          apiDeck.armyListContent[cardCategoryId] = {
            ...apiDeck.armyListContent[cardCategoryId],
            ...expand,
            qty: qty,
          };
        }

        setArmyListValidation({
          listStatus: apiDeck.listStatus,
          listErrors: apiDeck.listErrors,
        });

        if (COMPANY_FLAG === "shard-bugs") {
          if (apiDeck.army?.name) {
            let characterFilters = {
              faction: apiDeck.army?.name,
              cardType: "Cosmic Avatar",
              gameSystemId: apiDeck.gameSystem?.id,
            };
            let apiCharacters = await handlePagingAll(
              "/cards",
              characterFilters
            );
            setCharacters(apiCharacters);
          }
        }
      } catch (err) {
        postNotification("error", err);
      }
    } else {
      apiDeck = {
        ...blankDeck,
      };

      if (gameSystem || gs) {
        apiDeck.gameSystem = gameSystem || gs;
      }
    }

    apiDeck.tagsList = apiDeck?.tags?.length > 0 ? apiDeck.tags.join(",") : "";
    // setDeck(apiDeck);

    setArmyListContent(apiDeck.armyListContent);
    delete apiDeck.armyListContent;
    setDeckHeader(apiDeck);

    var apiArmies = await handlePagingAll(`/armies`, {
      gameType: apiDeck.gameSystem?.code || SINGLE_GAMETYPE,
    });
    setArmies(apiArmies);

    setDeckFormats(dFormats);

    setLoading(false);
    setReady(true);
  };

  useEffect(() => {
    const go = async () => {
      if (deckId) {
        loadPage();
      } else if (SINGLE_GAME_SYSTEM !== undefined) {
        let apiGameSystem = await getData(
          `/gamesystems/${SINGLE_GAME_SYSTEM}`,
          {}
        );
        setGameSystem(apiGameSystem);
        loadPage(apiGameSystem);
      } else {
        let apiGameSystems = await getGameSystems({
          supportsDeckBuilder: true,
        });
        setGameSystems(apiGameSystems);
        setLoading(false);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <Container
      sx={{
        pt: 2,
        pb: 2,
      }}
      maxWidth="lg"
    >
      {!ready && gameSystems && (
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <GameSystemPicker
                name="gameType"
                value={gameSystem?.code || ""}
                handleChange={(e) => {
                  if (e.target.value) {
                    gameSystems.forEach((gs) => {
                      if (gs.code === e.target.value) {
                        setGameSystem(gs);
                        loadPage(gs);
                        return true;
                      }
                    });
                  }
                }}
                gameSystems={gameSystems}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {ready && (
        <>
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary",
              }}
            >
              <Typography variant="h3">{heading || "Army Builder"}</Typography>
              {COMPANY_FLAG === "jasco" && (
                <Typography variant="caption">
                  Have any feedback on our deck builder? We'd love to hear your
                  thoughts.{" "}
                  <Link
                    href="https://w7dq12wcv1m.typeform.com/to/OOFk5lVt"
                    target="_blank"
                  >
                    Click here for more.
                  </Link>
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Grid container sx={{ gap: 2 }}>
                <Grid
                  item
                  xs={12}
                  lg={4}
                  sx={{
                    p: 2,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <DeckHeader
                    cardUtil={cardUtil}
                    deckHeader={deckHeader}
                    updateHeader={updateHeader}
                    armyListContent={armyListContent}
                    deckFormats={deckFormats}
                    armies={armies}
                    characters={characters}
                    setCharacters={setCharacters}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  lg={8}
                  sx={{
                    maxWidth: [
                      "100%",
                      "100%",
                      "100%",
                      `calc(${(100 / 3) * 2}% - 16px)`,
                    ], // deal with spacing bug
                  }}
                >
                  <Grid container sx={{ gap: 2 }}>
                    <Grid item xs={12}>
                      <CardSearch
                        gameSystem={gameSystem || deckHeader?.gameSystem}
                        cardUtil={cardUtil}
                        armies={armies}
                        cardRight={cardAdd}
                        cardLeft={cardRemove}
                        cardView={(c) => setCard(c)}
                        buttons
                        imageCache={imageCache}
                        deckBuilder
                        dense
                        deckFormat={deckFormat}
                        resultsHelperText={
                          isMobile
                            ? "Click to add to deck, or press and hold to preview or add to side"
                            : "Click to add to deck, or right click to preview or add to side"
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",
                      }}
                    >
                      <Grid container>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            p: 1,
                          }}
                        >
                          <Typography variant="h4">
                            Number of Cards: {getCardCount(armyListContent)}
                          </Typography>
                          {COMPANY_FLAG === "jasco" && (
                            <Typography variant="caption">
                              A legal deck must conatin at least{" "}
                              {(deckFormat?.deckSize?.min || 51) - 1} cards,
                              plus your starting character.
                            </Typography>
                          )}
                        </Grid>
                        {armyListContent &&
                          Object.values(armyListContent).length > 0 &&
                          Object.values(armyListContent)
                            .sort((a, b) => {
                              return a.order - b.order;
                            })
                            .map((cardCategory, index) => {
                              var sizeError =
                                (cardCategory.size?.min !== undefined &&
                                  (cardCategory.qty || 0) <
                                    cardCategory.size?.min) ||
                                (cardCategory.size?.max !== undefined &&
                                  (cardCategory.qty || 0) >
                                    cardCategory.size?.max);
                              return (
                                <Grid
                                  key={index}
                                  item
                                  xs={12}
                                  sx={{
                                    p: 1,
                                  }}
                                >
                                  <Grid container>
                                    <Grid
                                      item
                                      xs={12}
                                      sx={{
                                        p: 1,
                                        backgroundColor: sizeError
                                          ? "error.main"
                                          : "background.secondary",
                                        color: sizeError
                                          ? "error.contrastText"
                                          : null,
                                        borderRadius: sizeError ? 1 : null,
                                      }}
                                    >
                                      <Typography variant="h5">
                                        {`${cardCategory.name} (${
                                          cardCategory.qty || 0
                                        }${
                                          cardCategory.size?.max !== undefined
                                            ? `/${cardCategory.size.max}`
                                            : ""
                                        } card${
                                          cardCategory.qty !== 1 ? "s" : ""
                                        })`}
                                      </Typography>
                                    </Grid>
                                    {cardCategory.qty > 0 &&
                                      Object.values(cardCategory.cards).map(
                                        (card) => {
                                          let maxQty =
                                            card.maxQty !== undefined
                                              ? card.maxQty
                                              : deckFormat?.rarities?.[
                                                    card.rarity
                                                  ]?.size?.max !== undefined
                                                ? deckFormat?.rarities?.[
                                                    card.rarity
                                                  ]?.size?.max
                                                : deckFormat?.cards?.size?.max;
                                          let cardError = card.qty > maxQty;

                                          return (
                                            <Grid item xs={12}>
                                              <CardCard
                                                key={card.id}
                                                card={card}
                                                superDense
                                                leftClick={(c) => setCard(c)}
                                                error={cardError}
                                                imageCache={imageCache}
                                                buttons={
                                                  <ButtonGroup
                                                    sx={{
                                                      "& .MuiButtonGroup-grouped":
                                                        { minWidth: "unset" },
                                                      "& .MuiSvgIcon-root": {
                                                        fontSize: "0.8rem",
                                                      },
                                                    }}
                                                  >
                                                    <Button
                                                      sx={{
                                                        pl: "4px",
                                                        pr: "4px",
                                                      }}
                                                      color="primary"
                                                      variant="outlined"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        cardRemove({
                                                          ...card,
                                                          isSide:
                                                            cardCategory.name ===
                                                            "Side",
                                                        });
                                                      }}
                                                    >
                                                      <BCPMinus />
                                                    </Button>
                                                    <Button
                                                      sx={{
                                                        pl: "4px",
                                                        pr: "4px",
                                                      }}
                                                      color="primary"
                                                      variant="outlined"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        cardAdd({
                                                          ...card,
                                                          isSide:
                                                            cardCategory.name ===
                                                            "Side",
                                                        });
                                                      }}
                                                    >
                                                      <BCPPlus />
                                                    </Button>
                                                  </ButtonGroup>
                                                }
                                              />
                                            </Grid>
                                          );
                                        }
                                      )}
                                  </Grid>
                                </Grid>
                              );
                            })}
                      </Grid>
                    </Grid>

                    {armyListValidation && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                          borderRadius: 1,
                          boxShadow: 1,
                          backgroundColor: "background.secondary",
                        }}
                      >
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              p: 1,
                            }}
                          >
                            <Typography variant="h5">
                              List Status{" "}
                              {titleCase(armyListValidation?.listStatus || "")}
                              {armyListValidation?.listStatus === "pending" ? (
                                <CircularProgress size="16" />
                              ) : null}
                            </Typography>
                          </Grid>
                          {armyListValidation?.listErrors?.length > 0 &&
                            armyListValidation.listErrors.map(
                              (listError, index) => (
                                <Grid
                                  key={index}
                                  item
                                  xs={12}
                                  sx={{
                                    p: 1,
                                  }}
                                >
                                  {listError}
                                </Grid>
                              )
                            )}
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <Button
                    ref={saveBoxRef}
                    fullWidth
                    color="success"
                    variant="contained"
                    onClick={saveArmyList}
                  >
                    Save
                  </Button>
                </Grid>

                {!saveBoxInView && (
                  <Box
                    sx={{
                      position: "fixed",
                      zIndex: 999,
                      p: 2,
                      bgcolor: "background.paper",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      boxShadow: 3,
                      bottom: 0,
                      left: 0,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        fullWidth
                        color="success"
                        variant="contained"
                        onClick={saveArmyList}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                )}

                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <FirstHand
                    armyList={{
                      ...deckHeader,
                      armyListContent: armyListContent,
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <ArmyStats
                    cardUtil={cardUtil}
                    armyList={{
                      ...deckHeader,
                      armyListContent: armyListContent,
                    }}
                    deckFormat={deckFormat}
                    armies={armies}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <CardModal
            card={card}
            setCard={setCard}
            cardAdd={cardAdd}
            cardRemove={cardRemove}
          />
        </>
      )}

      {(loading || processing) && <ModalLoadingFull />}
    </Container>
  );
}

const FormObserver = (props) => {
  const { handleValues } = props;
  const { values } = useFormikContext();

  useEffect(() => {
    if (handleValues) handleValues(values);
    // eslint-disable-next-line
  }, [values]);

  return null;
};

const DeckHeader = (props) => {
  const {
    deckHeader,
    updateHeader,
    armyListContent,
    deckFormats,
    armies,
    characters,
    setCharacters,
  } = props;
  const [loadingCharacters, setLoadingCharactes] = useState(false);

  const updateFaction = async (armyId) => {
    if (COMPANY_FLAG === "shard-bugs") {
      setLoadingCharactes(true);
      let army = armies?.filter((el) => el.id === armyId)?.[0];
      if (army?.name) {
        let apiCharacters = await handlePagingAll("/cards", {
          faction: army?.name,
          cardType: "Cosmic Avatar",
          gameSystemId: deckHeader.gameSystemId,
        });
        setCharacters(apiCharacters);
      }

      setLoadingCharactes(false);
    }
  };

  useEffect(() => {
    const go = async () => {
      let characterCategory = armyListContent?.Character;
      let characterArray = characterCategory?.cards
        ? Object.values(characterCategory.cards)
        : characters;
      characterArray.sort((a, b) => {
        var nameA = a.name.toLowerCase();
        var nameB = b.name.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
      setCharacters(characterArray);
      if (
        !deckHeader?.characterId ||
        !characterArray.filter((el) => el.id === deckHeader?.characterId).length
      ) {
        await updateHeader({
          ...deckHeader,
          characterId: "",
          character: {},
          armyId: "",
        });
      }
    };

    go();
    // eslint-disable-next-line
  }, [armyListContent]);

  return (
    <Formik
      initialValues={{
        name: deckHeader?.name || blankDeck?.name || "",
        deckFormat: deckHeader?.deckFormat || blankDeck?.deckFormat || "",
        armyId: deckHeader?.armyId || "",
        characterId: deckHeader?.characterId || "",
        character: deckHeader?.character || {},
        tagsList: deckHeader?.tagsList || "",
        tags: deckHeader?.tags || [],
        public:
          deckHeader?.public !== undefined
            ? deckHeader.public
            : blankDeck?.public,
        master:
          deckHeader?.master !== undefined
            ? deckHeader.master
            : blankDeck?.master,
      }}
      enableReinitialize={true}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .required("required")
          .min(3, "Minimum 3 Characters!")
          .max(255, "Maximum 255 Characters!"),
        deckFormat: Yup.string().required("required"),
        armyId: Yup.string().test(
          "valid-resource-symbol",
          `${
            deckHeader?.gameSystem?.factionLabel
              ? deckHeader.gameSystem.factionLabel
              : COMPANY_FLAG === "jasco"
                ? "Resource Symbol"
                : COMPANY_FLAG === "shard-bugs"
                  ? "Aliance"
                  : "Faction"
          } does not match your selected ${
            deckHeader?.gameSystem?.subFactionLabel
              ? deckHeader.gameSystem.subFactionLabel
              : COMPANY_FLAG === "shard-bugs"
                ? "Cosmic Avatar"
                : "Character"
          }'s ${
            deckHeader?.gameSystem?.factionLabel
              ? deckHeader.gameSystem.factionLabel
              : COMPANY_FLAG === "jasco"
                ? "Resource Symbols"
                : COMPANY_FLAG === "shard-bugs"
                  ? "Aliance"
                  : "Faction"
          }`,
          (value, context) => {
            if (COMPANY_FLAG !== "jasco") {
              return true;
            }
            if (!context?.parent?.character?.resourceSymbols?.length) {
              return true;
            }
            let resource = context.parent.character.resourceSymbols.filter(
              (el) => el.id === value
            )[0];

            if (resource) return true;

            if (COMPANY_FLAG === "shard-bugs") {
              // return context.createError({
              //   message: `${
              //     COMPANY_FLAG === "jasco"
              //       ? "Resource Symbol"
              //       : "Faction"
              //   } does not match your selected Character's Resource Symbols, please select one of: ${context.parent.character.resourceSymbols
              //     .map((el) => el.name)
              //     .join(", ")}`,
              // });
            }

            return context.createError({
              message: `${
                COMPANY_FLAG === "jasco" ? "Resource Symbol" : "Faction"
              } does not match your selected Character's Resource Symbols, please select one of: ${context.parent.character.resourceSymbols
                .map((el) => el.name)
                .join(", ")}`,
            });
          }
        ),
      })}
      onSubmit={async () => {}}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        setFieldTouched,
      }) => (
        <form onSubmit={handleSubmit}>
          <FormObserver handleValues={async () => await updateHeader(values)} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Deck Name"
                value={values.name}
                variant="outlined"
                fullWidth
                margin="none"
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>

            <Grid item xs={12}>
              <TextFieldSelect
                list={Object.values(deckFormats || {})?.map((df) => df.name)}
                name="deckFormat"
                label="Format"
                value={values.deckFormat}
                fullWidth
                variant="outlined"
                margin="none"
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                error={Boolean(touched.deckFormat && errors.deckFormat)}
                helperText={touched.deckFormat && errors.deckFormat}
              />
            </Grid>

            <Grid item xs={12}>
              <TextFieldSelect
                list={armies?.map((army) => {
                  return { label: army.name, value: army.id };
                })}
                name="armyId"
                label={
                  deckHeader?.gameSystem?.factionLabel
                    ? deckHeader.gameSystem.factionLabel
                    : "Faction"
                }
                value={values.armyId}
                fullWidth
                variant="outlined"
                margin="none"
                clearAll
                setFieldValue={setFieldValue}
                onChange={(e) => {
                  let armyId = e.target.value;
                  setFieldValue(e);
                  updateFaction(armyId);
                }}
                setFieldTouched={setFieldTouched}
                error={Boolean(touched.armyId && errors.armyId)}
                helperText={touched.armyId && errors.armyId}
              />
            </Grid>

            {(COMPANY_FLAG === "jasco" || COMPANY_FLAG === "shard-bugs") && (
              <Grid item xs={12}>
                <TextFieldSelect
                  list={
                    loadingCharacters
                      ? []
                      : characters.map((character, index) => {
                          return {
                            value: character.id,
                            label: (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                <CardImagePreview card={character} superDense />
                                <Typography sx={{ ml: 2 }}>
                                  {character.name}
                                </Typography>
                              </Box>
                            ),
                          };
                        })
                  }
                  onChange={(e) => {
                    let character = characters?.filter(
                      (el) => el.id === e.target.value
                    )?.[0];
                    setFieldValue("character", character || {});
                  }}
                  name="characterId"
                  label={
                    deckHeader?.gameSystem?.subFactionLabel
                      ? deckHeader.gameSystem.subFactionLabel
                      : "Character"
                  }
                  value={values.characterId}
                  fullWidth
                  variant="outlined"
                  margin="none"
                  clearAll
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  error={Boolean(touched.characterId && errors.characterId)}
                  helperText={touched.characterId && errors.characterId}
                  disabled={loadingCharacters}
                  InputProps={
                    loadingCharacters
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <CircularProgress />
                            </InputAdornment>
                          ),
                        }
                      : null
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <MUITags
                label="Tags"
                value={values.tagsList}
                name="tagsList"
                valueArray={values.tags}
                nameArray="tags"
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                handleChange={handleChange}
                error={Boolean(touched.tagsList && errors.tagsList)}
                helperText={touched.tagsList && errors.tagsList}
                fullWidth
                variant="outlined"
                margin="none"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                label={
                  <Typography className="MuiFormControlLabel-label">
                    Public
                  </Typography>
                }
                sx={{
                  ml: 0,
                  width: "100%",
                  "& .MuiFormControlLabel-label": {
                    width: "calc(100% - 58px)",
                  },
                }}
                control={
                  <Switch
                    color="primary"
                    checked={values.public}
                    onChange={() => {
                      setFieldValue("public", !values.public);
                    }}
                  />
                }
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
